// 📋 앱 전역 설문 팝업 (2026-10-07 형 "설문 인원들한테 TASK 로 알림 — 클릭하면 설문조사 할 수 있는 팝업").
//   TASK 카드·홈 위젯의 /forms?id=… 링크는 utils/openExternal.navigateInternal 이 'tpt:open-form' 이벤트로 바꿔 보내고,
//   이 호스트가 화면 이동 없이 그 자리에서 응답(또는 관리자 검토·승인) 창을 띄운다. App 에 1번 장착(로그인 화면 포함, 미로그인이면 FormView 가 오류 안내).
//   모달 규칙: 입력이 있는 창이라 바깥 클릭으로 닫지 않는다 — ✕·ESC 만 (GUIDE·메모리 feedback_modal_close_policy).
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Spinner from '../Spinner';
import { useEscapeClose } from '../../hooks/useEscapeClose';

const FormView = lazy(() => import('./FormView'));

export const OPEN_FORM_EVENT = 'tpt:open-form';

/** 다른 화면에서 설문 팝업 열기 */
export function openFormPopup(id: string): void {
	try { window.dispatchEvent(new CustomEvent(OPEN_FORM_EVENT, { detail: { id } })); } catch { /* 구형 웹뷰 */ }
}

export default function FormPopupHost() {
	const { t } = useTranslation();
	const [id, setId] = useState<string | null>(null);
	const close = useCallback(() => setId(null), []);
	useEscapeClose(close, !!id);
	useEffect(() => {
		const w = window as unknown as { __tptFormHost?: boolean };
		w.__tptFormHost = true;   // navigateInternal 이 이 표식이 있을 때만 이벤트로 돌린다(없으면 종전대로 /forms 로 이동)
		const h = (e: Event) => { const v = (e as CustomEvent<{ id?: string }>).detail?.id; if (v) setId(String(v)); };
		window.addEventListener(OPEN_FORM_EVENT, h);
		return () => { window.removeEventListener(OPEN_FORM_EVENT, h); w.__tptFormHost = false; };
	}, []);
	if (!id) return null;
	const openEditor = () => {
		close();
		// 수정은 넓은 /forms 화면에서 — 데스크톱은 별도 창, 그 외는 새 탭
		void import('../../desktop/popout').then((m) => m.openFormsWindow(`edit=${encodeURIComponent(id)}`)).catch(() => { try { window.open(`/forms?edit=${encodeURIComponent(id)}`, '_blank'); } catch { /* */ } });
	};
	const openResults = () => {
		close();
		void import('../../desktop/popout').then((m) => m.openFormsWindow(`id=${encodeURIComponent(id)}&results=1`)).catch(() => { try { window.open(`/forms?id=${encodeURIComponent(id)}&results=1`, '_blank'); } catch { /* */ } });
	};
	return createPortal(
		<div className="fixed inset-0 z-[1100] overflow-y-auto bg-slate-900/45 p-3 sm:p-6" role="dialog" aria-modal="true" data-testid="form-popup">
			<div className="relative mx-auto max-w-3xl rounded-2xl bg-violet-50 p-3 shadow-2xl sm:p-4">
				<button type="button" onClick={close} aria-label={t('forms.cancel', { defaultValue: '취소' }) as string}
					className="absolute right-3 top-3 z-10 rounded-full bg-surface px-2 py-0.5 text-fg-muted shadow hover:text-fg" data-testid="form-popup-close">✕</button>
				<Suspense fallback={<div className="flex justify-center py-10"><Spinner /></div>}>
					<FormView key={id} id={id} onBack={close} onEdit={openEditor} onResults={openResults} />
				</Suspense>
			</div>
		</div>,
		document.body,
	);
}
